import * as React from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Search, Download, Phone, User, MapPin, Calendar, DollarSign, Sparkles, RefreshCw, MessageSquare } from 'lucide-react'
import {
  useWAFlowResponses,
  useWAFlowResponseStats,
  useWAFlowResponse,
  useWAFlowClosers,
  useAssignWAFlowResponse,
  useUpdateWAFlowResponseStatus,
  useUpdateWAFlowResponseNotes,
  getWAFlowResponseExportUrl,
} from '@/hooks/useWAFlowResponses'
import type {
  WAFlowResponsesListParams,
  WALeadScore,
  WALeadStatus,
} from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-flow-responses')({
  component: WhatsAppFlowResponsesPage,
})

// ============================================================================
// PAGE
// ============================================================================

function WhatsAppFlowResponsesPage() {
  const { data: stats } = useWAFlowResponseStats()

  const subtitle = stats
    ? `${stats.total} submissions · ${stats.byScore.hot} HOT · ${stats.byScore.warm} WARM · ${stats.byStatus.new} new`
    : 'Loading responses...'

  return (
    <div className="flex flex-col h-full">
      <Header title="Flow Responses" subtitle={subtitle} />
      <div className="flex-1 overflow-auto px-4 sm:px-6 lg:px-9 pb-8 space-y-4">
        <StatsCards />
        <FlowResponsesTable />
      </div>
    </div>
  )
}

// ============================================================================
// STATS CARDS
// ============================================================================

function StatsCards() {
  const { data: stats, isPending } = useWAFlowResponseStats()

  if (isPending || !stats) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    )
  }

  const cards = [
    { label: 'HOT', count: stats.byScore.hot, color: 'bg-red-500/10 text-red-600 border-red-500/30' },
    { label: 'WARM', count: stats.byScore.warm, color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
    { label: 'COLD', count: stats.byScore.cold, color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
    { label: 'Review', count: stats.byScore.review, color: 'bg-purple-500/10 text-purple-600 border-purple-500/30' },
    { label: 'Nurture (BF)', count: stats.byScore.nurture_bf, color: 'bg-slate-500/10 text-slate-600 border-slate-500/30' },
  ]

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {cards.map(card => (
        <div
          key={card.label}
          className={`rounded-xl border p-4 ${card.color}`}
        >
          <div className="text-xs font-semibold uppercase tracking-wider opacity-80">
            {card.label}
          </div>
          <div className="text-2xl font-bold mt-1">{card.count}</div>
        </div>
      ))}
    </div>
  )
}

// ============================================================================
// TABLE
// ============================================================================

function FlowResponsesTable() {
  const [search, setSearch] = React.useState('')
  const [score, setScore] = React.useState<WALeadScore | 'all'>('all')
  const [status, setStatus] = React.useState<WALeadStatus | 'all'>('all')
  const [page, setPage] = React.useState(1)
  const [selectedId, setSelectedId] = React.useState<number | null>(null)

  const params: WAFlowResponsesListParams = {
    page,
    limit: 25,
    search: search || undefined,
    score,
    status,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  }

  const { data, isPending, refetch, isFetching } = useWAFlowResponses(params)

  const rows = data?.data ?? []
  const pagination = data?.pagination

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            placeholder="Search phone, name, PNO..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select value={score} onValueChange={(v) => { setScore(v as WALeadScore | 'all'); setPage(1) }}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Score" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scores</SelectItem>
            <SelectItem value="hot">HOT</SelectItem>
            <SelectItem value="warm">WARM</SelectItem>
            <SelectItem value="cold">COLD</SelectItem>
            <SelectItem value="review">Review</SelectItem>
            <SelectItem value="nurture_bf">Nurture (BF)</SelectItem>
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={(v) => { setStatus(v as WALeadStatus | 'all'); setPage(1) }}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="attended">Attended</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="no_show">No show</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>

        <div className="flex-1" />

        <Button asChild variant="outline" size="sm">
          <a href={getWAFlowResponseExportUrl()} download>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </a>
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Score</TableHead>
              <TableHead>Patient / Phone</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Timing</TableHead>
              <TableHead>Budget</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[120px]">Submitted</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPending ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-text-muted py-12">
                  No flow responses yet. Submissions will appear here once patients complete the Flow.
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow
                  key={row.id}
                  onClick={() => setSelectedId(row.id)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <ScoreBadge score={row.leadScore} />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {row.patientDisplayName || row.patientName || row.contactDisplayName || '—'}
                    </div>
                    <div className="text-xs text-text-muted font-mono">{row.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{row.branchName || '—'}</TableCell>
                  <TableCell className="text-sm">{formatTiming(row.timing)}</TableCell>
                  <TableCell className="text-sm">{formatBudget(row.budget)}</TableCell>
                  <TableCell className="text-sm">
                    {row.assignedUserName || <span className="text-text-muted italic">Unassigned</span>}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={row.status} />
                  </TableCell>
                  <TableCell className="text-xs text-text-muted">
                    {row.createdAt ? formatRelative(row.createdAt) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <div>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            {' – '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <FlowResponseDetail id={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  )
}

// ============================================================================
// DETAIL SHEET
// ============================================================================

function FlowResponseDetail({ id, onClose }: { id: number | null; onClose: () => void }) {
  const { data, isPending } = useWAFlowResponse(id)
  const { data: closersData } = useWAFlowClosers()
  const assignMutation = useAssignWAFlowResponse()
  const statusMutation = useUpdateWAFlowResponseStatus()
  const notesMutation = useUpdateWAFlowResponseNotes()

  const [notes, setNotes] = React.useState('')
  const [notesDirty, setNotesDirty] = React.useState(false)

  React.useEffect(() => {
    if (data?.notes !== undefined) {
      setNotes(data.notes ?? '')
      setNotesDirty(false)
    }
  }, [data?.id, data?.notes])

  const closers = closersData?.data ?? []

  return (
    <Sheet open={id !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Lead Detail</SheetTitle>
          <SheetDescription>
            Manage this WhatsApp Flow submission
          </SheetDescription>
        </SheetHeader>

        {isPending || !data ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-20" />
            <Skeleton className="h-32" />
          </div>
        ) : (
          <div className="space-y-5 mt-4">
            {/* Score + Status */}
            <div className="flex items-center gap-2">
              <ScoreBadge score={data.leadScore} />
              <StatusBadge status={data.status} />
            </div>

            {/* Open Conversation CTA */}
            {data.conversationId && (
              <Button asChild variant="default" size="sm" className="w-full">
                <Link to="/whatsapp" search={{ id: data.conversationId }}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Open Conversation
                </Link>
              </Button>
            )}

            {/* Contact / Patient */}
            <section className="space-y-2 text-sm">
              <InfoRow icon={<User className="w-4 h-4" />} label="Name">
                {data.patient?.displayName || data.patient?.name || data.contact?.displayName || '—'}
              </InfoRow>
              <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone">
                <span className="font-mono">{data.contact?.waPhoneNumber || '—'}</span>
              </InfoRow>
              {data.patient?.pno && (
                <InfoRow icon={<Sparkles className="w-4 h-4" />} label="PNO">
                  {data.patient.pno}
                </InfoRow>
              )}
              <InfoRow icon={<MapPin className="w-4 h-4" />} label="Branch">
                {data.branch?.shortName || data.branch?.name || '—'}
              </InfoRow>
            </section>

            {/* Form answers */}
            <section className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-2">
                Submission
              </div>
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Usia">
                {formatUsia(data.usia)}
              </InfoRow>
              <InfoRow label="Breastfeeding">
                {formatBreastfeeding(data.breastfeeding)}
              </InfoRow>
              <InfoRow label="Concerns">
                <div className="flex flex-wrap gap-1">
                  {(data.concerns ?? []).map(c => (
                    <Badge key={c} variant="secondary" className="text-xs">{formatConcern(c)}</Badge>
                  ))}
                  {(!data.concerns || data.concerns.length === 0) && <span className="text-text-muted">—</span>}
                </div>
              </InfoRow>
              <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Budget">
                {formatBudget(data.budget)}
              </InfoRow>
              <InfoRow icon={<Calendar className="w-4 h-4" />} label="Timing">
                {formatTiming(data.timing)}
              </InfoRow>
            </section>

            {/* Assignment */}
            <section className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Assignment
              </div>
              <Select
                value={data.assignedUserId?.toString() ?? 'unassigned'}
                onValueChange={(v) => {
                  const uid = v === 'unassigned' ? null : Number(v)
                  assignMutation.mutate({ id: data.id, assignedUserId: uid })
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Assign closer..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {closers.map(c => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name} <span className="text-xs text-text-muted">({c.role})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </section>

            {/* Status actions */}
            <section className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Quick status
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusButton label="Contacted" target="contacted" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'contacted' } })} />
                <StatusButton label="Booked" target="booked" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'booked' } })} />
                <StatusButton label="Attended" target="attended" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'attended' } })} />
                <StatusButton label="Converted" target="converted" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'converted' } })} />
                <StatusButton label="No show" target="no_show" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'no_show' } })} />
                <StatusButton label="Lost" target="lost" current={data.status} onClick={() => statusMutation.mutate({ id: data.id, data: { status: 'lost' } })} variant="destructive" />
              </div>
            </section>

            {/* Notes */}
            <section className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                Notes
              </div>
              <Textarea
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value)
                  setNotesDirty(true)
                }}
                placeholder="Closer notes — call summary, blocker, objection..."
                rows={4}
              />
              {notesDirty && (
                <Button
                  size="sm"
                  onClick={() => {
                    notesMutation.mutate(
                      { id: data.id, notes },
                      { onSuccess: () => setNotesDirty(false) }
                    )
                  }}
                  disabled={notesMutation.isPending}
                >
                  {notesMutation.isPending ? 'Saving...' : 'Save notes'}
                </Button>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ============================================================================
// HELPERS
// ============================================================================

function ScoreBadge({ score }: { score: WALeadScore }) {
  const styles: Record<WALeadScore, string> = {
    hot: 'bg-red-500/10 text-red-600 border-red-500/30',
    warm: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    cold: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    nurture_bf: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
    review: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
  }
  const label: Record<WALeadScore, string> = {
    hot: 'HOT',
    warm: 'WARM',
    cold: 'COLD',
    nurture_bf: 'Nurture',
    review: 'Review',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border ${styles[score]}`}>
      {label[score]}
    </span>
  )
}

function StatusBadge({ status }: { status: WALeadStatus }) {
  const styles: Record<WALeadStatus, string> = {
    new: 'bg-muted text-foreground',
    assigned: 'bg-sky-500/10 text-sky-600',
    contacted: 'bg-indigo-500/10 text-indigo-600',
    booked: 'bg-emerald-500/10 text-emerald-600',
    attended: 'bg-teal-500/10 text-teal-600',
    converted: 'bg-green-500/20 text-green-700 font-semibold',
    no_show: 'bg-orange-500/10 text-orange-600',
    lost: 'bg-rose-500/10 text-rose-600',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${styles[status]}`}>
      {status.replace('_', ' ')}
    </span>
  )
}

function StatusButton({
  label,
  target,
  current,
  onClick,
  variant = 'outline',
}: {
  label: string
  target: WALeadStatus
  current: WALeadStatus
  onClick: () => void
  variant?: 'outline' | 'destructive'
}) {
  const isCurrent = current === target
  return (
    <Button
      size="sm"
      variant={isCurrent ? 'default' : variant}
      onClick={onClick}
      disabled={isCurrent}
    >
      {label}
    </Button>
  )
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2">
      {icon && <span className="mt-0.5 text-text-muted">{icon}</span>}
      <div className="flex-1">
        <div className="text-xs text-text-muted">{label}</div>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  )
}

function formatTiming(v: string | null): string {
  if (!v) return '—'
  const map: Record<string, string> = {
    minggu_ni: 'Minggu ni',
    minggu_depan: 'Minggu depan',
    bulan_mei: 'Bulan Mei',
    bulan_depan: 'Bulan depan',
    belum_pasti: 'Belum pasti',
  }
  return map[v] ?? v
}

function formatBudget(v: string | null): string {
  if (!v) return '—'
  const map: Record<string, string> = {
    bawah_3k: '< RM3k',
    '3k_6k': 'RM3k – 6k',
    atas_6k: '> RM6k',
    belum_pasti: 'Nak tau dulu',
  }
  return map[v] ?? v
}

function formatUsia(v: string | null): string {
  if (!v) return '—'
  const map: Record<string, string> = {
    bawah_28: '< 28',
    '28_35': '28 – 35',
    '36_42': '36 – 42',
    '43_50': '43 – 50',
    '50_keatas': '> 50',
  }
  return map[v] ?? v
}

function formatBreastfeeding(v: string | null): string {
  if (!v) return '—'
  const map: Record<string, string> = {
    masih_menyusu: 'Masih menyusu',
    selesai_6bulan: 'Selesai >6 bulan',
    tak_pernah: 'Tak pernah',
  }
  return map[v] ?? v
}

function formatConcern(v: string): string {
  const map: Record<string, string> = {
    kendur_menyusu: 'Kendur selepas bersalin',
    mengecil_turun_berat: 'Mengecil selepas turun berat',
    hilang_shape_umur: 'Hilang shape sebab umur',
    firmness_lebih: 'Nak firmness lebih',
  }
  return map[v] ?? v
}

function formatRelative(iso: string): string {
  const then = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - then.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.floor(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  const diffD = Math.floor(diffH / 24)
  if (diffD < 7) return `${diffD}d ago`
  return then.toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
}
